/** A composer draft, kept per user and event so a reload or a session loss never loses text
 * (docs/plans/done/ui-u3-plan.md section 3, "Draft storage"). The bearer token never goes in here. */
export type ComposerDraft = {
  draftId: string;
  subject: string;
  body: string;
  questions: string;
  nextSteps: string;
  ack: boolean;
  /** Absent on drafts saved before "Send as" existed: persona. */
  sendAs?: "persona" | "self";
  /** Cc / Bcc as typed (comma separated); used only when sending as yourself. */
  cc?: string;
  bcc?: string;
  updatedAt: number;
};

const PREFIX = "vs-draft:";
const EXPIRY_MS = 7 * 24 * 60 * 60 * 1000;

function key(userId: string, eventId: string): string {
  return `${PREFIX}${userId}:${eventId}`;
}

export function loadDraft(userId: string, eventId: string): ComposerDraft | null {
  try {
    const raw = localStorage.getItem(key(userId, eventId));
    if (!raw) return null;
    const draft = JSON.parse(raw) as ComposerDraft;
    if (Date.now() - draft.updatedAt > EXPIRY_MS) {
      localStorage.removeItem(key(userId, eventId));
      return null;
    }
    return draft;
  } catch {
    return null; // private window, cleared site data, or a corrupt value
  }
}

export function saveDraft(userId: string, eventId: string, draft: ComposerDraft) {
  try {
    localStorage.setItem(key(userId, eventId), JSON.stringify(draft));
  } catch {
    /* storage full or blocked: the draft just lives in memory for this render */
  }
}

export function removeDraft(userId: string, eventId: string) {
  try {
    localStorage.removeItem(key(userId, eventId));
  } catch {
    /* ignore */
  }
}

/** Called on sign-out (and when a stale token is rejected on load): nothing kept here is the
 * next person's business. With a user id, only that user's drafts go (leaving training). */
export function clearAllDrafts(userId?: string) {
  const prefix = userId ? `${PREFIX}${userId}:` : PREFIX;
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k?.startsWith(prefix)) localStorage.removeItem(k);
    }
  } catch {
    /* ignore */
  }
}
